import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { landlordAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'landlord') {
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    try {
      const response = await landlordAPI.getSubscriptionStatus();
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const refreshStatus = useCallback(() => {
    setLoading(true);
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Everything below is derived from subscriptionStatus, so compute it inside a memo
  // keyed on the primitives that actually change. This gives consumers a stable value
  // object and satisfies exhaustive-deps.
  const value = useMemo(() => {
    const isTrialActive = subscriptionStatus?.is_trial && !subscriptionStatus?.trial_expired;
    const isTrialExpired = subscriptionStatus?.is_trial && subscriptionStatus?.trial_expired;
    const isSubscriptionActive = subscriptionStatus?.is_active;
    const featuresLocked = subscriptionStatus?.features_locked;
    const trialDaysRemaining = subscriptionStatus?.days_remaining || 0;

    const canUseFeature = () => {
      if (!subscriptionStatus) return true; // Allow if status not loaded
      if (featuresLocked) return false;
      return true;
    };

    const canAddProperty = () => {
      if (!subscriptionStatus) return true;
      if (featuresLocked) return false;
      const { usage, property_limit } = subscriptionStatus;
      if (property_limit === -1) return true;
      return (usage?.properties || 0) < property_limit;
    };

    const canAddUnit = () => {
      if (!subscriptionStatus) return true;
      if (featuresLocked) return false;
      const { usage, unit_limit } = subscriptionStatus;
      if (unit_limit === -1) return true;
      return (usage?.units || 0) < unit_limit;
    };

    return {
      subscriptionStatus,
      loading,
      refreshStatus,
      isTrialActive,
      isTrialExpired,
      isSubscriptionActive,
      featuresLocked,
      trialDaysRemaining,
      canUseFeature,
      canAddProperty,
      canAddUnit,
    };
  }, [subscriptionStatus, loading, refreshStatus]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    // Return default values if not within provider (for non-landlord users)
    return {
      subscriptionStatus: null,
      loading: false,
      refreshStatus: () => {},
      isTrialActive: false,
      isTrialExpired: false,
      isSubscriptionActive: true,
      featuresLocked: false,
      trialDaysRemaining: 0,
      canUseFeature: () => true,
      canAddProperty: () => true,
      canAddUnit: () => true,
    };
  }
  return context;
};

export default SubscriptionContext;
